import { RCustomerLookupEntityType } from '@shared-features/enums/shared-enums.enum';

/** Who owns a location. `type` is what a shipment payload keys every receiver field off. */
export interface LocationOwner {
  id: number;
  name: string | null;
  type: RCustomerLookupEntityType;
}

/**
 * The owner fields a location may carry; one from `LookupType.Locations` carries none of them.
 *
 * Declared structurally rather than `Pick`ed from `BulkLocation`: this util is also called with a
 * `LocationModel` and with the synthetic option `onAddLocation` builds, neither of which is a
 * `BulkLocation` — and `@shared` must not reach into a feature module, which the barrel that
 * `@shared-features` imports would otherwise drag along.
 *
 * `id` is not read. It is here so the type is not weak: an all-optional type rejects an argument
 * sharing none of its properties, which is every caller passing a location with no owner.
 */
interface OwnableLocation {
  id?: number;
  customerId?: number;
  customerName?: string;
  receiverId?: number;
  receiverName?: string;
}

/**
 * The customer or receiver a location belongs to, or null for one that names neither — which is the
 * expected answer for a pickup stop, not a failure.
 *
 * The endpoint fills one pair and nulls the other, so the order below is not a precedence rule.
 */
export function locationOwner(location: OwnableLocation | null | undefined): LocationOwner | null {
  if (location?.receiverId) {
    return {
      id: location.receiverId,
      name: location.receiverName ?? null,
      type: RCustomerLookupEntityType.Receiver,
    };
  }
  if (location?.customerId) {
    return {
      id: location.customerId,
      name: location.customerName ?? null,
      type: RCustomerLookupEntityType.Customer,
    };
  }
  return null;
}
